import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Upload, X, Paperclip, Loader2, Eye, Download, FileText, Image } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isImage = (ct) => ct && ct.startsWith('image/');
const isPDF = (ct) => ct === 'application/pdf';

const FilePreviewModal = ({ file, onClose }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!file) return;
    const token = localStorage.getItem('token');
    fetch(`${API}/files/${file.file_id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        setSrc(URL.createObjectURL(blob));
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => { if (src) URL.revokeObjectURL(src); };
  }, [file]);

  if (!file) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70" onClick={onClose}>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            {isImage(file.content_type) ? <Image className="w-4 h-4 text-blue-500" /> : <FileText className="w-4 h-4 text-slate-500" />}
            <span className="font-medium text-sm text-slate-700">{file.filename}</span>
          </div>
          <div className="flex items-center gap-2">
            {src && (
              <a href={src} download={file.filename} className="flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid="preview-download">
                <Download className="w-3.5 h-3.5" /> Download
              </a>
            )}
            <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="preview-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="overflow-auto max-h-[75vh] p-4 flex items-center justify-center bg-slate-50">
          {loading ? (
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          ) : src && isImage(file.content_type) ? (
            <img src={src} alt={file.filename} className="max-w-full max-h-full rounded-lg shadow" data-testid="preview-image" />
          ) : src && isPDF(file.content_type) ? (
            <iframe src={src} title={file.filename} className="w-full h-[65vh] rounded border" data-testid="preview-pdf" />
          ) : (
            <div className="text-center text-slate-500 py-8">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Preview not available for this file type</p>
              {src && <a href={src} download={file.filename} className="mt-2 inline-block text-sm text-blue-600 hover:underline">Download to view</a>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TaskModal = ({ isOpen, onClose, onSubmit, task }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'General',
    priority: 'Medium',
    status: 'pending',
    due_date: '',
    assigned_to: '',
    vendor_name: '',
    vendor_contact: '',
    work_assignment: 'Site',
    attachments: [],
  });
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || '',
        category: task.category,
        priority: task.priority,
        status: task.status,
        due_date: task.due_date || '',
        assigned_to: task.assigned_to,
        vendor_name: task.vendor_name || '',
        vendor_contact: task.vendor_contact || '',
        work_assignment: task.work_assignment,
        attachments: task.attachments || [],
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: 'General',
        priority: 'Medium',
        status: 'pending',
        due_date: '',
        assigned_to: '',
        vendor_name: '',
        vendor_contact: '',
        work_assignment: 'Site',
        attachments: [],
      });
    }
  }, [task, isOpen]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('token');

    try {
      const uploadedFiles = [];
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        uploadedFiles.push(response.data);
      }

      setFormData({
        ...formData,
        attachments: [...formData.attachments, ...uploadedFiles],
      });
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload file(s)');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index) => {
    setFormData({
      ...formData,
      attachments: formData.attachments.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-modal" aria-describedby="task-modal-description">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold">
            {task ? 'Edit Task' : 'Create New Task'}
          </DialogTitle>
          <p id="task-modal-description" className="sr-only">
            {task ? 'Edit task details including title, description, category, priority, and other attributes' : 'Create a new task with details including title, description, category, priority, and other attributes'}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="title" className="font-medium">
              Task Title *
            </Label>
            <Input
              id="title"
              data-testid="task-title-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="description" className="font-medium">
              Description
            </Label>
            <Textarea
              id="description"
              data-testid="task-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="mt-1.5"
              placeholder="Optional task description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category" className="font-medium">
                Category *
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="mt-1.5" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Truck Section">Truck Section</SelectItem>
                  <SelectItem value="Machinery Section">Machinery Section</SelectItem>
                  <SelectItem value="General">General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="priority" className="font-medium">
                Priority *
              </Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger className="mt-1.5" data-testid="priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Critical">Critical</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status" className="font-medium">
                Status *
              </Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="mt-1.5" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="order-placed">Order Placed</SelectItem>
                  <SelectItem value="in-transit">In Transit</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="due_date" className="font-medium">
                Due Date
              </Label>
              <Input
                id="due_date"
                data-testid="due-date-input"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="mt-1.5"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="assigned_to" className="font-medium">
              Assigned To *
            </Label>
            <Input
              id="assigned_to"
              data-testid="assigned-to-input"
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              required
              placeholder="Employee name or team"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="vendor_name" className="font-medium">
              Vendor Name *
            </Label>
            <Input
              id="vendor_name"
              data-testid="vendor-name-input"
              value={formData.vendor_name}
              onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
              required
              placeholder="Vendor or supplier name"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="vendor_contact" className="font-medium">
              Vendor Contact Number *
            </Label>
            <Input
              id="vendor_contact"
              data-testid="vendor-contact-input"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formData.vendor_contact}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setFormData({ ...formData, vendor_contact: value });
              }}
              required
              placeholder="Phone number (numbers only)"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="work_assignment" className="font-medium">
              Work Assignment *
            </Label>
            <Select
              value={formData.work_assignment}
              onValueChange={(value) => setFormData({ ...formData, work_assignment: value })}
            >
              <SelectTrigger className="mt-1.5" data-testid="work-assignment-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Site">Site</SelectItem>
                <SelectItem value="Head Office">Head Office</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="font-medium mb-2 block">
              Attachments (Proof of Completion)
            </Label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-slate-400 transition-colors">
              <input
                type="file"
                id="file-upload"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
                data-testid="file-upload-input"
              />
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center gap-2 cursor-pointer"
              >
                {uploading ? (
                  <Loader2 className="h-8 w-8 text-slate-400 animate-spin" />
                ) : (
                  <Upload className="h-8 w-8 text-slate-400" />
                )}
                <p className="text-sm text-slate-600">
                  {uploading ? 'Uploading...' : 'Click to upload files or drag and drop'}
                </p>
                <p className="text-xs text-slate-500">PDF, images, documents</p>
              </label>
            </div>

            {formData.attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                {formData.attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded border border-slate-200 hover:bg-slate-100 transition-colors"
                    data-testid={`attached-file-${index}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isImage(file.content_type) ? (
                        <Image className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      ) : isPDF(file.content_type) ? (
                        <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
                      ) : (
                        <Paperclip className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      )}
                      <span className="text-sm text-slate-700 truncate max-w-[200px]">{file.filename}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {file.file_id && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setPreviewFile(file)}
                          data-testid={`preview-attachment-${index}`}
                          className="text-blue-600 hover:bg-blue-50"
                          title="Preview"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeAttachment(index)}
                        data-testid={`remove-attachment-${index}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-task-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="submit-task-button"
              disabled={uploading}
            >
              {task ? 'Update Task' : 'Create Task'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
    </>
  );
};

export default TaskModal;
